import type { PropsWithChildren } from 'react';
import { styled } from 'styled-components';

import { tk, Typography, zIndex } from '@sunwebgroup/ui-components';
import { buildImgSrc, buildImgSrcSet } from '@/helpers/image-url';
import { useEnv } from '@/scopes/common/infrastructure/env';
import { useIntl } from '@/intl';
import type { CatalogueType as ConfigCatalogueType } from '@/config/types';

const Wrapper = styled.div`
  width: 100%;
  user-select: none;
  background: ${tk('color_semantic_bg_disabled')};
  display: flex;
  position: relative;
  margin-bottom: 238px;
  @media ${tk('mq_tablet')} {
    margin-bottom: 163px;
  }
  @media ${tk('mq_desktop_md')} {
    margin-bottom: 48px;
  }
`;

const Content = styled.div`
  top: 0;
  left: 0;
  position: absolute;
  user-select: none;
  width: 100%;
  z-index: ${zIndex.heroContent};
`;

const BackgroundImage = styled.img`
  object-fit: cover;
  width: 100%;
  height: 360px;

  @media ${tk('mq_tablet')} {
    height: 480px;
  }

  @media (min-width: 460px) {
    object-position: 50% 21%;
  }
`;
const HeroTitle = styled(Typography)`
  color: #fff;
  text-align: center;
  text-shadow:
    0 0 2px rgba(0, 0, 0, 0.06),
    0 1px 4px rgba(0, 0, 0, 0.08),
    0 2px 8px rgba(0, 0, 0, 0.1); //shadow not existing in current tokens
  left: 0;
  right: 0;
  margin: 0 auto;
  position: absolute;
  top: 40px;
  max-width: 280px;
  @media ${tk('mq_tablet')} {
    ${Typography.modVariant('heading-2xl')}
    top: 80px;
    max-width: unset;
  }
  @media ${tk('mq_desktop_md')} {
    font-size: 48px; //size not existing in current tokens
    top: 127px;
  }
`;

export function Hero({
  useGenericTitle,
  catalogue,
  children,
}: { useGenericTitle: boolean; catalogue: ConfigCatalogueType } & PropsWithChildren) {
  const intl = useIntl();
  const mediaServerBaseUrl = useEnv('MEDIA_SERVER_BASE_URL');
  const imageType = catalogue === 'summer' ? 'bg_sun' : 'bg_winter';
  const imageUrl = `${mediaServerBaseUrl}/${imageType}.jpg`;
  const title = useGenericTitle
    ? intl.formatMessage({ id: 'quicksearch_hero_title' })
    : intl.formatMessage({ id: `quicksearch_hero_title_${catalogue}` });

  return (
    <Wrapper>
      <HeroTitle $variant={'heading-xl'}>{title}</HeroTitle>
      <BackgroundImage
        src={buildImgSrc(imageUrl, 480)}
        srcSet={buildImgSrcSet(imageUrl, [480, 600, 720, 1440, 1440, 2000, 2400])}
        sizes="100vw"
        alt="" // intentionally empty, only for decoration
        // @ts-expect-error -- Not part of the types yet, but this is an img attribute in modern browsers
        fetchpriority="high"
      />
      <Content>{children}</Content>
    </Wrapper>
  );
}
